import { Document, Page, Text, View } from '@react-pdf/renderer';
import { PeriodoLotacaoColaboradorTexto } from '../relatorio-colaboradores.utils';
import { RelatorioComarcasColaboradores, RelatorioPredioColaboradores } from '../relatorio-colaboradores.types';
import { styles } from '@/modules/business/lotacoes/reports/styles/estagiario-por-setor.style';

interface IProps {
  comarcas: RelatorioComarcasColaboradores[];
}

const colLeft = { textAlign: 'left' as const };

function totalEquipe(predio: RelatorioPredioColaboradores) {
  return (predio.setores ?? []).reduce(
    (acc, setor) => acc + (setor.colaboradores?.length || 0),
    0,
  );
}

function RenderizarPrediosDaComarca(comarca: RelatorioComarcasColaboradores) {
  const elementos: JSX.Element[] = [];

  comarca.predios?.forEach((predio, index) => {
    if (!predio.setores || predio.setores.length === 0) return;

    const total = totalEquipe(predio);

    elementos.push(
      <View key={`predio-${index}`} style={styles.section} wrap={false}>
        <View style={[styles.fullRow, styles.fullCellGrey1]}>
          <Text style={styles.fullCell}>{predio.predio_nome}</Text>
        </View>
        <View style={[styles.fullRow, styles.fullCellGrey]}>
          <Text style={[styles.fullCell, { textAlign: 'left', fontSize: 8, fontStyle: 'italic', flex: 1 }]}>
            {`${predio.endereco_logradouro || ''}, nº ${predio.endereco_numero || ''}, ${predio.endereco_bairro || ''}. CEP: ${predio.endereco_cep || ''}. ${predio.endereco_cidade || ''}`}
          </Text>
          <Text style={{ textAlign: 'right', fontSize: 9, padding: 5 }}>
            {`Total: ${total}`}
          </Text>
        </View>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableColHeader, styles.tableColHeaderSetor]}>SETOR DE ATUAÇÃO</Text>
            <Text style={[styles.tableColHeader, styles.tableColHeaderEquipe]}>EQUIPE</Text>
          </View>

          {predio.setores.map((setor, idx) => (
            <View
              key={`setor-${idx}`}
              style={[styles.tableRow, idx % 2 === 0 ? styles.tableRowOdd : styles.tableRowEven]}
              wrap={false}
            >
              <View style={[styles.tableCol, styles.tableColSetor, colLeft]}>
                <Text>{setor.nome}</Text>
              </View>
              <View style={[styles.tableCol, styles.tableColEquipe, colLeft]}>
                {setor.colaboradores.length > 0 ? (
                  <View style={styles.subTable}>
                    {setor.colaboradores.map((c, i) => {
                      const periodo = PeriodoLotacaoColaboradorTexto(
                        c.lotacoes_data_inicial,
                        c.lotacoes_data_final,
                      );
                      return (
                        <View key={`c-${i}`} style={styles.subTableRow}>
                          <Text style={c.diretor ? { fontFamily: 'Helvetica-Bold' } : undefined}>
                            {c.colaborador_nome}
                            {periodo ? (
                              <Text style={styles.textMuted}>{` ${periodo}`}</Text>
                            ) : null}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <Text> </Text>
                )}
              </View>
            </View>
          ))}
        </View>
      </View>,
    );
  });

  return elementos;
}

export function ColaboradorPorSetorPdf({ comarcas }: IProps) {
  const comarcasVisiveis = (comarcas ?? []).filter(
    (comarca) =>
      comarca.predios?.some((predio) => Array.isArray(predio.setores) && predio.setores.length > 0),
  );

  if (comarcasVisiveis.length === 0) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>
            DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE - TABELA DE LOTAÇÃO
          </Text>
          <Text style={styles.emptyText}>Nenhum dado disponível</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {comarcasVisiveis.map((comarca, index) => (
        <Page key={index} size="A4" style={styles.page} orientation="portrait" wrap>
          <Text style={styles.title}>
            DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE - TABELA DE LOTAÇÃO
          </Text>
          <View style={styles.section}>
            <View style={[styles.fullRow, styles.fullCellGrey]}>
              <Text style={[styles.fullCell, styles.comarcaHeader]}>{comarca.comarca_nome}</Text>
            </View>
            {RenderizarPrediosDaComarca(comarca)}
          </View>
          <Text
            style={{
              position: 'absolute',
              fontSize: 10,
              bottom: 10,
              left: 0,
              right: 0,
              textAlign: 'center',
            }}
            render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
            fixed
          />
        </Page>
      ))}
    </Document>
  );
}
